var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { DefaultMinDecibels, DefaultMaxDecibels, DefaultFftSize, } from "./constants.js";
import { Mapping } from "../coordinates/mapping.js";
import { Zoom, DefaultZoom } from "../coordinates/zoom.js";
let RrScopeBackground = class RrScopeBackground extends LitElement {
    static get styles() {
        return [
            css `
        canvas {
          color: var(--rr-captions-color, rgba(255, 255, 255, 0.5));
          width: 100%;
          height: 100%;
        }
      `,
        ];
    }
    render() {
        return html `<canvas id="canvas"></canvas>`;
    }
    constructor() {
        super();
        this.centerFrequency = 0;
        this.frequencyScale = 1;
        this.minDecibels = DefaultMinDecibels;
        this.maxDecibels = DefaultMaxDecibels;
        this.fftSize = DefaultFftSize;
        this.zoom = DefaultZoom;
        this.resizeObserver = new ResizeObserver(() => this.redraw());
    }
    connectedCallback() {
        super.connectedCallback();
        this.resizeObserver?.disconnect();
        this.resizeObserver.observe(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver?.disconnect();
    }
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.redraw();
    }
    updated(changed) {
        super.updated(changed);
        if (changed.size == 0 || (changed.size == 1 && changed.has("lines"))) {
            return;
        }
        this.redraw();
    }
    redraw() {
        let ctx = this.getContext();
        if (!ctx)
            return;
        let canvas = ctx.canvas;
        if (canvas.width != canvas.offsetWidth)
            canvas.width = this.offsetWidth;
        if (canvas.height != canvas.offsetHeight)
            canvas.height = this.offsetHeight;
        const width = canvas.width;
        const height = canvas.height;
        const topSpace = 16;
        const leftSpace = 24;
        const color = getComputedStyle(ctx.canvas).getPropertyValue("color");
        const lines = this.computeLines(width - leftSpace, height - topSpace);
        ctx.clearRect(0, 0, width, height);
        // Draw the text.
        ctx.save();
        ctx.fillStyle = color;
        for (let { position, value, horizontal } of lines) {
            let [x, y] = [
                leftSpace + position * (width - leftSpace),
                topSpace + position * (height - topSpace),
            ];
            let text = String(horizontal ? value : value / (this.frequencyScale || 1));
            if (horizontal) {
                ctx.textBaseline = "middle";
                ctx.textAlign = "right";
                x = leftSpace - 2;
                let m = ctx.measureText(text);
                if (y - m.actualBoundingBoxAscent < topSpace)
                    y = m.actualBoundingBoxAscent + topSpace;
                if (y + m.actualBoundingBoxDescent > height)
                    y = height - m.actualBoundingBoxDescent;
            }
            else {
                ctx.textBaseline = "bottom";
                ctx.textAlign = "center";
                y = topSpace - 2;
                let m = ctx.measureText(text);
                if (x - m.actualBoundingBoxLeft < leftSpace)
                    x = m.actualBoundingBoxLeft + leftSpace;
                if (x + m.actualBoundingBoxRight > width)
                    x = width - m.actualBoundingBoxRight;
            }
            ctx.fillText(text, x, y);
        }
        ctx.restore();
        // Draw the lines.
        ctx.save();
        ctx.strokeStyle = color;
        ctx.beginPath();
        for (let { position, horizontal } of lines) {
            if (horizontal) {
                let y = topSpace + position * (height - topSpace);
                ctx.moveTo(leftSpace, y);
                ctx.lineTo(width, y);
            }
            else {
                let x = leftSpace + position * (width - leftSpace);
                ctx.moveTo(x, topSpace);
                ctx.lineTo(x, height);
            }
        }
        ctx.stroke();
        ctx.restore();
    }
    getContext() {
        if (this.context)
            return this.context;
        if (!this.canvas)
            return;
        this.context = this.canvas.getContext("2d");
        return this.context;
    }
    computeLines(width, height) {
        let lines = [];
        if (this.minDecibels !== undefined && this.maxDecibels !== undefined) {
            lines.push(...getGridLines(this.minDecibels, this.maxDecibels, 20, 25, height, Direction.Descending, Orientation.Horizontal, [1, 2, 3, 5, 6, 10]));
        }
        if (this.bandwidth !== undefined) {
            const mapping = new Mapping(this.zoom, 1, this.fftSize, this.centerFrequency, this.bandwidth);
            lines.push(...getGridLines(mapping.leftFrequency, mapping.rightFrequency, 50, 80, width, Direction.Ascending, Orientation.Vertical));
        }
        else {
            const position = this.zoom.zoomed(0.5);
            if (position >= 0 && position <= 1) {
                lines.push({
                    value: this.centerFrequency,
                    position,
                    horizontal: false,
                });
            }
        }
        return lines;
    }
};
__decorate([
    property({ type: Number, reflect: true }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "bandwidth", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "center-frequency" }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "centerFrequency", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "frequency-scale" }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "frequencyScale", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "min-decibels" }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "minDecibels", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "max-decibels" }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "maxDecibels", void 0);
__decorate([
    property({ type: Number, reflect: true }),
    __metadata("design:type", Number)
], RrScopeBackground.prototype, "fftSize", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", typeof (_a = typeof Zoom !== "undefined" && Zoom) === "function" ? _a : Object)
], RrScopeBackground.prototype, "zoom", void 0);
__decorate([
    query("#canvas"),
    __metadata("design:type", typeof (_b = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _b : Object)
], RrScopeBackground.prototype, "canvas", void 0);
RrScopeBackground = __decorate([
    customElement("rr-scope-background"),
    __metadata("design:paramtypes", [])
], RrScopeBackground);
export { RrScopeBackground };
var Direction;
(function (Direction) {
    Direction[Direction["Ascending"] = 0] = "Ascending";
    Direction[Direction["Descending"] = 1] = "Descending";
})(Direction || (Direction = {}));
var Orientation;
(function (Orientation) {
    Orientation[Orientation["Horizontal"] = 0] = "Horizontal";
    Orientation[Orientation["Vertical"] = 1] = "Vertical";
})(Orientation || (Orientation = {}));
function getGridLines(min, max, minSep, maxSep, sepReference, direction, orientation, divisions = [1, 2, 5, 10]) {
    const range = max - min;
    const magnitude = Math.pow(10, Math.floor(Math.log10(range / 2)));
    const division = getBestDivision(minSep / sepReference, maxSep / sepReference, range, magnitude, divisions);
    const horizontal = orientation == Orientation.Horizontal;
    let lines = [];
    let value = min;
    if (value % division != 0)
        value += division - (value % division);
    while (value <= max) {
        const position = direction == Direction.Ascending
            ? (value - min) / range
            : (max - value) / range;
        lines.push({ position, value, horizontal });
        value += division;
    }
    return lines;
}
function getBestDivision(minSep, maxSep, range, magnitude, divisions) {
    const leastDivision = (range * minSep) / magnitude;
    const mostDivision = (range * maxSep) / magnitude;
    const middleDivision = (leastDivision + mostDivision) / 2;
    if (mostDivision < divisions[0])
        return getBestDivision(minSep, maxSep, range, magnitude / 10, divisions);
    if (leastDivision > divisions[divisions.length - 1])
        return getBestDivision(minSep, maxSep, range, magnitude * 10, divisions);
    let middlest = divisions[0];
    let midDistance = Math.abs(middlest - middleDivision);
    let midInDiv = middlest >= leastDivision && middlest <= mostDivision;
    for (let i = 1; i < divisions.length; ++i) {
        let inDiv = divisions[i] >= leastDivision && divisions[i] <= mostDivision;
        if (midInDiv && !inDiv)
            continue;
        let distance = Math.abs(divisions[i] - middleDivision);
        if (distance < midDistance) {
            middlest = divisions[i];
            midDistance = distance;
            midInDiv = inDiv;
        }
    }
    return middlest * magnitude;
}
